import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Key, 
  Copy, 
  Check, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Lock, 
  ShieldCheck, 
  Zap, 
  Layers, 
  Download,
  Info,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';

export const KeyGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [activeTab, setActiveTab] = useState('password'); // password, token, uuid
  
  // 1. PASSWORD GENERATOR STATES
  const [password, setPassword] = useState('');
  const [passLength, setPassLength] = useState(16); // 6 to 64
  const [includeUpper, setIncludeUpper] = useState(true);
  const [includeLower, setIncludeLower] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(false); // i, l, 1, o, 0, O etc
  const [strengthInfo, setStrengthInfo] = useState({ score: 0, label: 'Weak', color: '#ef4444', bits: 0, crackTime: 'Instant' });
  const [bulkList, setBulkList] = useState([]);
  const [bulkCount, setBulkCount] = useState(5); // generate 5 or 10

  // 2. TOKEN GENERATOR STATES
  const [token, setToken] = useState('');
  const [tokenLength, setTokenLength] = useState(32);
  const [tokenFormat, setTokenFormat] = useState('hex'); // hex, base64, base32

  // 3. UUID GENERATOR STATES
  const [uuidList, setUuidList] = useState([]);
  const [uuidCount, setUuidCount] = useState(5);

  const [isCopied, setIsCopied] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Generate single secure password based on selected parameters
  const generatePasswordString = (len) => {
    let charset = '';
    if (includeLower) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeUpper) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:\',./<>?';

    if (excludeSimilar) {
      // Remove easily confused letters/numbers: i, l, 1, I, o, 0, O
      charset = charset.replace(/[il1IKeyo0O]/g, '');
    }

    if (charset.length === 0) return '';

    // Cryptographically secure random values array filling
    const array = new Uint32Array(len);
    window.crypto.getRandomValues(array);
    
    let result = '';
    for (let i = 0; i < len; i++) {
      result += charset[array[i] % charset.length];
    }
    return result;
  };

  // Evaluate password strength metrics
  const evaluatePasswordStrength = (pass) => {
    if (!pass) return;

    let poolSize = 0;
    if (/[a-z]/.test(pass)) poolSize += 26;
    if (/[A-Z]/.test(pass)) poolSize += 26;
    if (/[0-9]/.test(pass)) poolSize += 10;
    if (/[^a-zA-Z0-9]/.test(pass)) poolSize += 32;

    const bits = Math.round(pass.length * Math.log2(poolSize || 1));
    
    let score = 0; // 0 to 4
    if (bits >= 40) score = 1;
    if (bits >= 60) score = 2;
    if (bits >= 80) score = 3;
    if (bits >= 100) score = 4;

    let label = 'Weak';
    let color = '#ef4444';
    let crackTime = 'Instant';

    if (score === 1) {
      label = 'Weak (Low Entropy)';
      color = '#f97316';
      crackTime = 'A few minutes';
    } else if (score === 2) {
      label = 'Fair (Decent protection)';
      color = '#eab308';
      crackTime = 'A few days';
    } else if (score === 3) {
      label = 'Strong (Highly secure)';
      color = '#22c55e';
      crackTime = 'Years / Decades';
    } else if (score === 4) {
      label = 'Military Grade (Bulletproof)';
      color = '#06b6d4';
      crackTime = 'Trillions of centuries';
    }

    setStrengthInfo({ score, label, color, bits, crackTime });
  };

  // Triggers password regeneration
  const refreshPassword = () => {
    const mainPass = generatePasswordString(passLength);
    setPassword(mainPass);
    evaluatePasswordStrength(mainPass);

    // Generate bulk list
    const list = [];
    for (let i = 0; i < bulkCount; i++) {
      list.push(generatePasswordString(passLength));
    }
    setBulkList(list);
  };

  // 2. TOKEN ENCODING GENERATORS
  const refreshCryptoToken = () => {
    const numBytes = Math.ceil(tokenLength / 2);
    const array = new Uint8Array(numBytes);
    window.crypto.getRandomValues(array);

    let formatted = '';
    if (tokenFormat === 'hex') {
      formatted = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    } else if (tokenFormat === 'base64') {
      // Clean base64 output
      const binaryString = String.fromCharCode.apply(null, array);
      formatted = btoa(binaryString).replace(/[^a-zA-Z0-9]/g, '');
    } else {
      // Base32 alphanumeric representation
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
      let binStr = '';
      for (let i = 0; i < array.length; i++) {
        binStr += array[i].toString(2).padStart(8, '0');
      }
      for (let j = 0; j < binStr.length; j += 5) {
        const chunk = binStr.substring(j, j + 5);
        if (chunk.length === 5) {
          formatted += alphabet[parseInt(chunk, 2)];
        }
      }
    }

    setToken(formatted.substring(0, tokenLength));
  };

  // 3. CRYPTO UUID (v4) GENERATOR
  const refreshUUIDs = () => {
    const list = [];
    for (let i = 0; i < uuidCount; i++) {
      // Secure crypto UUID generation
      const uuid = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
      list.push(uuid);
    }
    setUuidList(list);
  };

  // Run automatically on load or trigger dependencies change
  useEffect(() => {
    if (activeTab === 'password') {
      refreshPassword();
    } else if (activeTab === 'token') {
      refreshCryptoToken();
    } else {
      refreshUUIDs();
    }
  }, [activeTab, passLength, includeUpper, includeLower, includeNumbers, includeSymbols, excludeSimilar, bulkCount, tokenLength, tokenFormat, uuidCount]);

  // Copy handler
  const copyToClipboard = (text, index = null) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    
    if (index !== null) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } else {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleDownloadPasswords = () => {
    const textContent = [password, ...bulkList].join('\n');
    const blob = new Blob([textContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.download = 'secure-keys-list.txt';
    link.href = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadUUIDs = () => {
    const blob = new Blob([uuidList.join('\n')], { type: 'text/plain' });
    const link = document.createElement('a');
    link.download = 'uuidv4-keys.txt';
    link.href = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Sleek Key & Password Creator</span>
      </div>


      {/* Tabs Menu */}
      <div style={{
        display: 'flex',
        background: 'rgba(15, 23, 42, 0.04)',
        padding: '4px',
        borderRadius: '14px',
        gap: '4px',
        marginBottom: '32px',
        maxWidth: '540px'
      }}>
        <button
          onClick={() => setActiveTab('password')}
          style={{
            flex: 1,
            background: activeTab === 'password' ? '#ffffff' : 'transparent',
            color: activeTab === 'password' ? '#a855f7' : 'var(--text-secondary)',
            border: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: '12px 16px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'var(--transition-smooth)',
            boxShadow: activeTab === 'password' ? 'var(--shadow-sm)' : 'none'
          }}
        >
          Secure Password
        </button>
        <button
          onClick={() => setActiveTab('token')}
          style={{
            flex: 1,
            background: activeTab === 'token' ? '#ffffff' : 'transparent',
            color: activeTab === 'token' ? '#a855f7' : 'var(--text-secondary)',
            border: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: '12px 16px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'var(--transition-smooth)',
            boxShadow: activeTab === 'token' ? 'var(--shadow-sm)' : 'none'
          }}
        >
          Crypto Token
        </button>
        <button
          onClick={() => setActiveTab('uuid')}
          style={{
            flex: 1,
            background: activeTab === 'uuid' ? '#ffffff' : 'transparent',
            color: activeTab === 'uuid' ? '#a855f7' : 'var(--text-secondary)',
            border: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: '12px 16px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'var(--transition-smooth)',
            boxShadow: activeTab === 'uuid' ? 'var(--shadow-sm)' : 'none'
          }}
        >
          UUID v4 Key
        </button>
      </div>

      {/* Tabs Layout contents */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="keys-workspace-grid">
        
        {/* Left column: Configuration parameters */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* TAB 1. PASSWORD WORKSPACE */}
          {activeTab === 'password' && (
            <div style={cardStyle}>
              {/* Output Display panel */}
              <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '24px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                boxShadow: 'var(--shadow-sm)'
              }}>
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '1.4rem',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  wordBreak: 'break-all',
                  userSelect: 'all'
                }}>
                  {password || 'Select options...'}
                </span>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => copyToClipboard(password)}
                    style={actionBtnStyle}
                    title="Copy Password"
                  >
                    {isCopied ? <Check size={18} style={{ color: '#059669' }} /> : <Copy size={18} />}
                  </button>
                  <button
                    onClick={refreshPassword}
                    style={actionBtnStyle}
                    title="Generate New"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>

              {/* Password Strength Evaluator Meter */}
              <div style={{
                background: 'rgba(15, 23, 42, 0.02)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Password Strength</span>
                  <span style={{ fontSize: '0.825rem', fontWeight: 800, color: strengthInfo.color }}>{strengthInfo.label}</span>
                </div>
                {/* Visual bar */}
                <div style={{
                  height: '6px',
                  background: 'rgba(15, 23, 42, 0.06)',
                  borderRadius: '99px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${(strengthInfo.score / 4) * 100}%`,
                    background: strengthInfo.color,
                    borderRadius: '99px',
                    transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                  }} />
                </div>
                {/* Secondary indicators */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>
                  <span>Entropy: <strong>{strengthInfo.bits} bits</strong></span>
                  <span>Estimated crack delay: <strong>{strengthInfo.crackTime}</strong></span>
                </div>
              </div>

              {/* Sliders and checklist parameter row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px',
                marginTop: '10px'
              }}>
                {/* Sliders group */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={inputGroupStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={labelStyle}>Password Length</label>
                      <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#a855f7', background: 'rgba(168, 85, 247, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                        {passLength} chars
                      </span>
                    </div>
                    <input 
                      type="range"
                      min="6"
                      max="64"
                      value={passLength}
                      onChange={(e) => setPassLength(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer' }}
                    />
                  </div>

                  <div style={inputGroupStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={labelStyle}>Bulk List Count</label>
                      <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-secondary)' }}>{bulkCount} alternatives</span>
                    </div>
                    <input 
                      type="range"
                      min="2"
                      max="15"
                      value={bulkCount}
                      onChange={(e) => setBulkCount(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: 'var(--text-secondary)', cursor: 'pointer' }}
                    />
                  </div>
                </div>

                {/* Character checklist group */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <label style={labelStyle}>Complexity Rules</label>
                  
                  <div style={checkRowStyle}>
                    <input type="checkbox" id="check-lower" checked={includeLower} onChange={(e) => setIncludeLower(e.target.checked)} style={checkboxStyle} />
                    <label htmlFor="check-lower" style={checkLabelStyle}>Include Lowercase (a-z)</label>
                  </div>

                  <div style={checkRowStyle}>
                    <input type="checkbox" id="check-upper" checked={includeUpper} onChange={(e) => setIncludeUpper(e.target.checked)} style={checkboxStyle} />
                    <label htmlFor="check-upper" style={checkLabelStyle}>Include Uppercase (A-Z)</label>
                  </div>

                  <div style={checkRowStyle}>
                    <input type="checkbox" id="check-numbers" checked={includeNumbers} onChange={(e) => setIncludeNumbers(e.target.checked)} style={checkboxStyle} />
                    <label htmlFor="check-numbers" style={checkLabelStyle}>Include Numbers (0-9)</label>
                  </div>

                  <div style={checkRowStyle}>
                    <input type="checkbox" id="check-symbols" checked={includeSymbols} onChange={(e) => setIncludeSymbols(e.target.checked)} style={checkboxStyle} />
                    <label htmlFor="check-symbols" style={checkLabelStyle}>Include Symbols (!@#$%^&*)</label>
                  </div>

                  <div style={checkRowStyle}>
                    <input type="checkbox" id="check-similar" checked={excludeSimilar} onChange={(e) => setExcludeSimilar(e.target.checked)} style={checkboxStyle} />
                    <label htmlFor="check-similar" style={checkLabelStyle}>Exclude Similar Characters (i, 1, l, o, 0)</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2. CRYPTO TOKENS WORKSPACE */}
          {activeTab === 'token' && (
            <div style={cardStyle}>
              <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '24px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                boxShadow: 'var(--shadow-sm)'
              }}>
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  wordBreak: 'break-all',
                  userSelect: 'all'
                }}>
                  {token || 'Select options...'}
                </span>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => copyToClipboard(token)}
                    style={actionBtnStyle}
                    title="Copy Token"
                  >
                    {isCopied ? <Check size={18} style={{ color: '#059669' }} /> : <Copy size={18} />}
                  </button>
                  <button
                    onClick={refreshCryptoToken}
                    style={actionBtnStyle}
                    title="Generate New"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>

              {/* Sliders group */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px',
                marginTop: '10px'
              }}>
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Token Character Length</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#a855f7', background: 'rgba(168, 85, 247, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {tokenLength} chars
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="16"
                    max="128"
                    step="8"
                    value={tokenLength}
                    onChange={(e) => setTokenLength(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer' }}
                  />
                </div>

                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Encoding Output format</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    {['hex', 'base64', 'base32'].map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => setTokenFormat(fmt)}
                        style={{
                          height: '38px',
                          border: tokenFormat === fmt ? '1.5px solid #a855f7' : '1px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          background: tokenFormat === fmt ? 'rgba(168, 85, 247, 0.05)' : '#ffffff',
                          color: tokenFormat === fmt ? '#a855f7' : 'var(--text-secondary)',
                          textTransform: 'uppercase',
                          transition: 'var(--transition-smooth)'
                        }}
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3. UUID WORKSPACE */}
          {activeTab === 'uuid' && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>UUID v4 Keys Batch</h3>
                <button
                  onClick={refreshUUIDs}
                  style={{
                    border: 'none',
                    background: 'rgba(168, 85, 247, 0.06)',
                    color: '#a855f7',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'var(--transition-smooth)'
                  }}
                >
                  <RefreshCw size={14} /> Refresh UUID Batch
                </button>
              </div>

              {/* UUID Count slider */}
              <div style={inputGroupStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={labelStyle}>Bulk UUID Count</label>
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#a855f7', background: 'rgba(168, 85, 247, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                    {uuidCount} units
                  </span>
                </div>
                <input 
                  type="range"
                  min="2"
                  max="15"
                  value={uuidCount}
                  onChange={(e) => setUuidCount(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer', maxWidth: '300px' }}
                />
              </div>

              {/* Display UUID list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                {uuidList.map((uuid, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '12px 18px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontFamily: 'monospace',
                      fontSize: '0.95rem',
                      fontWeight: 700
                    }}
                  >
                    <span style={{ color: 'var(--text-primary)' }}>{uuid}</span>
                    <button
                      onClick={() => copyToClipboard(uuid, index)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px'
                      }}
                    >
                      {copiedIndex === index ? <Check size={16} style={{ color: '#059669' }} /> : <Copy size={16} />}
                    </button>
                  </div>
                ))}
              </div>

              {/* Download batch list */}
              {uuidList.length > 0 && (
                <>
                  <AdToast message={toast} />
                  <button
                    onClick={withAd('uuid-download', handleDownloadUUIDs)}
                  style={{
                    alignSelf: 'flex-start',
                    background: '#ffffff',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '12px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="download-btn"
                >
                  <Download size={14} /> Download batch file (.txt)
                  </button>
                </>
              )}
            </div>
          )}

          {/* BULK ALTERNATIVES PANEL FOR PASSWORD */}
          {activeTab === 'password' && bulkList.length > 0 && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Alternative Password options
                </h4>
                <button
                  onClick={withAd('pwd-download', handleDownloadPasswords)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#a855f7',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Download size={14} /> Download All (.txt)
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {bulkList.map((p, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '12px 18px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontFamily: 'monospace',
                      fontSize: '0.95rem',
                      fontWeight: 700
                    }}
                  >
                    <span style={{ color: 'var(--text-primary)' }}>{p}</span>
                    <button
                      onClick={() => copyToClipboard(p, idx)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px'
                      }}
                    >
                      {copiedIndex === idx ? <Check size={16} style={{ color: '#059669' }} /> : <Copy size={16} />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Safety disclaimer details */}


        </div>

      </div>

      {/* Styled custom button hover parameters */}
      <style>{`
        .download-btn:hover {
          background: rgba(15, 23, 42, 0.04) !important;
          border-color: var(--text-secondary) !important;
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="key-generator" />
    </div>
  );
};

// Internal Style profiles
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  boxShadow: 'var(--shadow-sm)'
};

const actionBtnStyle = {
  border: '1px solid var(--border-color)',
  background: '#ffffff',
  color: 'var(--text-secondary)',
  width: '38px',
  height: '38px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'var(--transition-smooth)'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const checkRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

const checkboxStyle = {
  width: '18px',
  height: '18px',
  cursor: 'pointer',
  accentColor: '#a855f7'
};

const checkLabelStyle = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  cursor: 'pointer'
};

export default KeyGenerator;
